import { Card, Col, List, Row, Space, Spin, Typography, Image, message } from "antd";
import { httpRequest } from "../../../helpers/api";
import { ESocialMedia } from "../../../types/socialMedia.type";
import { FacebookIcon, TelegramIcon, TwitterIcon, WhatsappIcon, LineIcon } from "next-share";
import Instagram from "../../../assets/images/instagram.png";
import OtherShareIcon from "../../../assets/Icons/icon-other-share.svg";
import { LinkOutlined, VerticalRightOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import StatisticTopArticlesTable from "./StatisticTopArticlesTable";
import StatisticTopListingTable from "./StatisticTopListingTable";
import useAuth from "../../../hooks/useAuth";
import { getErrorMessage } from "../../../helpers/errorHandler";
import { SharedDataProps } from "./ShareWidget";

type IProps = {
	contentType: string;
	contentId?: string;
	showTable?: boolean;
	sharesDataProps?: SharedDataProps;
};

const { Title, Text } = Typography;

const StatisticSharedCard: React.FC<IProps> = ({ contentType, contentId, showTable, sharesDataProps }) => {
	const { officeId, employeeId, isHQ, isAdminHQAndAdminOffices, isFO, officeIdList } = useAuth();
	const [sharesData, setsharesData] = useState<SharedDataProps | undefined>();
	const [isLoading, setIsLoading] = useState(false);

	async function newFetchShares() {
		setIsLoading(true);

		try {
			const params = {
				contentType: contentType,
				contentId: contentId || "",
				refId1: isHQ ? undefined : isFO ? officeIdList.join(",") : officeId,
				refId2: isAdminHQAndAdminOffices ? undefined : employeeId,
				refId3: "",
				limit: 5,
			};
			const res = await httpRequest.get<any>(
				isHQ ? "content-shared/statistic" : "office/content-shared/statistic",
				{
					params,
				}
			);

			setsharesData(res.data.payload);
		} catch (error) {
			message.error(getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	}

	useEffect(() => {
		if (sharesDataProps) {
			setsharesData(sharesDataProps);
		} else {
			newFetchShares();
		}
	}, [
		contentType,
		contentId,
		isHQ,
		isFO,
		officeIdList,
		officeId,
		isAdminHQAndAdminOffices,
		employeeId,
		sharesDataProps,
	]);

	const socialMediaData = Object.entries(ESocialMedia)
		.map(([key, value]) => {
			let count = sharesData?.totalSharedPerSocmed?.[value] || 0;
			if (key === 'instagram') {
				const instagramStoriesCount = sharesData?.totalSharedPerSocmed?.['instagramstories'] || 0;
				count += instagramStoriesCount;
			}
			return {
				key,
				value,
				count,
			};
		}).filter((item) => item.key !== 'instagramStories');

	function capitalizeSentence(string: string) {
		return string.charAt(0).toUpperCase() + string.slice(1);
	}

	function addThousandSeparators(number: number): string {
		return number.toLocaleString();
	}

	const renderIcon = (platform: string) => {
		switch (platform) {
			case ESocialMedia.facebook:
				return (
					<FacebookIcon
						size={28}
						round
					/>
				);
			case ESocialMedia.telegram:
				return (
					<TelegramIcon
						size={28}
						round
					/>
				);
			case ESocialMedia.twitter:
				return (
					<TwitterIcon
						size={28}
						round
					/>
				);
			case ESocialMedia.whatsapp:
				return (
					<WhatsappIcon
						size={28}
						round
					/>
				);
			case ESocialMedia.line:
				return (
					<LineIcon
						size={28}
						round
					/>
				);
			case ESocialMedia.instagram:
				return (
					<Image
						src={Instagram}
						alt="instagram"
						width={28}
						height={28}
						preview={false}
					/>
				);
			case ESocialMedia.copyLink:
				return <LinkOutlined />;
			case ESocialMedia.other:
				return (
					<Image
						src={OtherShareIcon}
						alt="other"
						width={20}
						height={20}
						preview={false}
					/>
				);
		}
	};

	return (
		<Spin spinning={isLoading}>
			<Card
				size="small"
				loading={isLoading}
				bordered={false}
				style={{ borderRadius: 20 }}
			>
				<Title level={3}>Total {capitalizeSentence(contentType)} Shares</Title>
				{/* <Text>{`${officeId} - ${employeeId}`}</Text> */}
				<Row>
					<Col span={6}>
						<Space direction="vertical">
							<Text type="secondary">Total Shares</Text>
							<Text
								strong
								style={{ fontSize: 24 }}
							>
								{sharesData ? addThousandSeparators(sharesData?.totalSharedCount) : ""}
							</Text>
							{showTable === true && (
								<List
									grid={{ gutter: 12, column: 2 }}
									itemLayout="horizontal"
									dataSource={socialMediaData}
									renderItem={(item) => (
										<List.Item key={item.key}>
											<Space
												direction="vertical"
												size={4}
											>
												<Text type="secondary">{capitalizeSentence(item.value)}</Text>
												<Space
													direction="horizontal"
													size={8}
												>
													<div>{renderIcon(item.value)}</div>
													<Text
														strong
														style={{ fontSize: 24 }}
													>
														{item.count.toString()}
													</Text>
												</Space>
											</Space>
										</List.Item>
									)}
								/>
							)}
						</Space>
					</Col>
					{!showTable && (
						<Col span={18}>
							<List
								grid={{ gutter: 12, column: 4, sm: 2, xs: 2 }}
								itemLayout="horizontal"
								dataSource={socialMediaData}
								renderItem={(item) => (
									<List.Item key={item.key}>
										<Space
											direction="vertical"
											size={4}
										>
											<Text type="secondary">{capitalizeSentence(item.value)}</Text>
											<Space
												direction="horizontal"
												size={8}
											>
												<div>{renderIcon(item.value)}</div>
												<Text
													strong
													style={{ fontSize: 24 }}
												>
													{item.count.toString()}
												</Text>
											</Space>
										</Space>
									</List.Item>
								)}
							/>
						</Col>
					)}
					{showTable === true && (
						<Col span={18}>
							<Space direction="vertical">
								<Text type="secondary">Most Shared {capitalizeSentence(contentType)}</Text>
								{contentType === "article" && (
									<StatisticTopArticlesTable
										contentType="article"
										tableType="shares"
										dataProps={sharesData?.results ?? []}
									/>
								)}
								{contentType === "listing" && (
									<StatisticTopListingTable
										contentType="listing"
										tableType="shares"
										dataProps={sharesData?.results ?? []}
									/>
								)}
							</Space>
						</Col>
					)}
				</Row>
			</Card>
		</Spin>
	);
};

export default StatisticSharedCard;
